import { Controller } from "@hotwired/stimulus"

/**
 * Toggles between two install command variants (macOS/Linux curl vs
 * Windows PowerShell). On connect, detects the user's platform and
 * shows the matching variant. The "Show other OS" link animates the
 * swap with a fade.
 *
 * Usage:
 *   <div data-controller="install-command">
 *     <div data-install-command-target="unix">curl ... | sh</div>
 *     <div data-install-command-target="windows" hidden>irm ... | iex</div>
 *     <button data-action="install-command#toggle"
 *             data-install-command-target="toggle">
 *       Show <span data-install-command-target="toggleLabel">Windows</span>
 *     </button>
 *   </div>
 */
export default class InstallCommandController extends Controller {
  static targets = ["unix", "windows", "toggle", "toggleLabel"]

  declare readonly unixTarget: HTMLElement
  declare readonly windowsTarget: HTMLElement
  declare readonly toggleLabelTarget: HTMLElement

  private current: "unix" | "windows" = "unix"

  connect() {
    const detected = this.detectPlatform()
    this.current = detected
    this.applyVisibility(false)
  }

  toggle(event: Event) {
    event.preventDefault()
    this.current = this.current === "unix" ? "windows" : "unix"
    this.applyVisibility(true)
  }

  private detectPlatform(): "unix" | "windows" {
    const ua = (navigator.userAgent || "").toLowerCase()
    const platform = (navigator.platform || "").toLowerCase()
    if (ua.includes("windows") || platform.includes("win")) {
      return "windows"
    }
    return "unix"
  }

  private applyVisibility(animate: boolean) {
    const show = this.current === "unix" ? this.unixTarget : this.windowsTarget
    const hide = this.current === "unix" ? this.windowsTarget : this.unixTarget
    const otherLabel = this.current === "unix" ? "Windows" : "macOS / Linux"
    this.toggleLabelTarget.textContent = otherLabel

    if (!animate) {
      show.hidden = false
      hide.hidden = true
      return
    }

    hide.classList.add("install-command-fade-out")
    const finishHide = () => {
      hide.hidden = true
      hide.classList.remove("install-command-fade-out")
      show.hidden = false
      show.classList.add("install-command-fade-in")
      const finishShow = () => {
        show.classList.remove("install-command-fade-in")
        show.removeEventListener("animationend", finishShow)
      }
      show.addEventListener("animationend", finishShow)
    }
    hide.addEventListener("animationend", finishHide, { once: true })
  }
}
